import { usePermissao } from "@/contexts/autorizacao/permissao";
import { useEstabelecimento } from "@/contexts/cadastro/estabelecimento";
import { STATUS_RAM } from "@/contexts/fiscalizacao/relatorioAM/model/STATUS_RAM";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { ClearOutlined, FilterOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Row,
  Select,
  Space,
} from "antd";
import { useEffect } from "react";
import { useSearchParams } from "react-router";

interface FormValues {
  estabelecimento: {
    id: number;
  };
  situacao: (typeof STATUS_RAM)[keyof typeof STATUS_RAM];
}

export function FiltroRelatoriosAM() {
  const { isOnline } = useNetworkStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const { estabelecimentoOptions } = useEstabelecimento();
  const { hasEnabledPermissao } = usePermissao();
  const [form] = Form.useForm<FormValues>();
  const { message } = App.useApp();

  const handleClear = () => {
    form.resetFields();
    searchParams.delete("estabelecimentoId");
    searchParams.delete("situacao");
    searchParams.delete("page");
    setSearchParams(searchParams);

    message.info({ content: "Filtro limpo", key: "limpar-filtro" });
  };

  const handleSubmit = (values: FormValues) => {
    if (values.estabelecimento?.id) {
      searchParams.set("estabelecimentoId", String(values.estabelecimento.id));
    } else {
      searchParams.delete("estabelecimentoId");
    }

    if (values.situacao) {
      searchParams.set("situacao", values.situacao);
    } else {
      searchParams.delete("situacao");
    }

    searchParams.set("page", "1");
    setSearchParams(searchParams);

    message.success({
      content: "Filtro aplicado com sucesso",
      key: "aplicar-filtro",
    });
  };

  useEffect(() => {
    const estabelecimentoId = searchParams.get("estabelecimentoId");
    const situacao = searchParams.get("situacao") as FormValues["situacao"];

    form.setFieldsValue({
      estabelecimento: {
        id: Number(estabelecimentoId) || undefined,
      },
      situacao: situacao || undefined,
    });
  }, []);

  return (
    <>
      <Card
        styles={{
          body: {
            backgroundColor: "#f0f2f5",
            borderRadius: 0,
          },
          actions: {
            backgroundColor: "#fafafa",
            borderRadius: 0,
          },
        }}
        actions={[
          <Space key="actions">
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              htmlType="button"
            >
              Limpar
            </Button>

            <Button
              form="form-filtro"
              type="primary"
              htmlType="submit"
              icon={<FilterOutlined />}
              disabled={!isOnline || !hasEnabledPermissao("RAM_VISUALIZAR")}
            >
              Aplicar
            </Button>
          </Space>,
        ]}
        style={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}
      >
        <Card.Meta title="Filtro" />

        <Divider />

        <Form
          id="form-filtro"
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name={["estabelecimento", "id"]}
                rules={[
                  { required: true, message: "Selecione um estabelecimento" },
                ]}
              >
                <Select
                  placeholder="Estabelecimento"
                  showSearch
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  optionFilterProp="label"
                  options={estabelecimentoOptions}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="situacao">
                <Select placeholder="Situação" allowClear>
                  <Select.Option value={"PENDENTE"}>Pendente</Select.Option>
                  <Select.Option value={"CANCELADO"}>Cancelado</Select.Option>
                  <Select.Option value={"FINALIZADO"}>Finalizado</Select.Option>
                  <Select.Option value={"AGUARDANDO_PLANO_DE_ACAO"}>
                    Aguardando Plano de Ação
                  </Select.Option>
                  <Select.Option value={"AGUARDANDO_PARECER_PLANO_DE_ACAO"}>
                    Aguardando Parecer
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  );
}
